import { guuid, isFunction, isNeString, isObject } from '@aretheyhappy/utils';
import DashboardConfigPopover from '../popover/DashboardConfigPopover.vue';

/**
 * Expected properties and functions for widgets that appear on dashboards.
 * If your widget is the final display widget bound on DashboardService.registerDashboardWidgetComponent(),
 * then you should use this mixin inside the widget being bound.
 * It provides helper props and methods for use in your widget.
 */
export default {
    props: {
        // This value turns to true when the dashboard is in edit mode or in the configuration preview.
        // Widgets should use this to hide/show different features.
        preview: {type: Boolean, default: false},
        // What type of widget this is
        widgetType: {type: String, default: ''},
        // Type of dashboard we are working with
        dashboardType: {type: String, default: ''},
        // Entire Dashboard object
        dashboard: {type: Object, default: () => ({})},
    },
    data () {
        return {
            uid: guuid(),
            // Widget config converted to internal config for usage in the widgets.
            internal_config: {},
            // Widget original config, used for resetting config
            original_config: {}
        }
    },
    methods: {
        /**
         * Show configuration popover for this widget. Widgets should check
         * hasFilterConfiguration before showing the button to open configuration.
         */
        openConfigPopover(event) {
            const config = new MeadCommon.PopoverConfig()
                .setComponent(DashboardConfigPopover)
                .setMovable(true)
                .setNoPad(true)
                .setMobileFull(true)
                .setEvent(event)
                .setProps({
                    childProps: Object.assign({}, this.internal_config),
                    childType: this.widgetType,
                    dashboardType: this.dashboardType,
                    dashboard: this.dashboard,
                    pubkey: this.uid,
                })
            MeadCommon.PopoverService.open(config);
        },
        /**
         * Updates from DashboardConfigPopover.vue
         */
        onConfigUpdated(new_config) {
            if (isObject(new_config)) {
                // Copy changed config into internal config
                for (const key in new_config) {
                    this.internal_config[key] = new_config[key];
                }

                // Ensure computed properties update
                this.internal_config = Object.assign({}, this.internal_config);

                // Below is used if the consumer widget is using MetricsFetchingMixin
                if (isFunction(this.reload)) this.reload();
            }
        },
        /**
         * Returns true if this widget should show filter configuration
         * @returns
         */
        hasFilterConfiguration() {
            const widgetDefinition = MeadCommon.DashboardService.getWidgetDefinition(this.widgetType);
            return widgetDefinition !== undefined && widgetDefinition.hasFilterConfiguration === true;
        }
    },
    created() {
        if (!isNeString(this.widgetType))
            console.warn('widgetType not defined: ', this.$options._componentTag);

        // Ensure widget has all the base values to render (helps with edge cases
        // when new config values are added)
        const default_config = isNeString(this.widgetType) && MeadCommon.DashboardService.hasWidgetDefinition(this.widgetType)
            ? MeadCommon.DashboardService.getWidgetDefinition(this.widgetType).getDefaultProps() : {};
        const config = Object.assign({}, default_config);;

        // Props that were sent in from parent
        const propsData = this.$options.propsData || {};

        // Copy props into internal config. Configs are added directly as props to widgets
        // so that they are compatible outside of the dashboard system.
        for (const propKey of Object.keys(this.$props || {})) {
            if (['preview', 'widgetType', 'dashboardType', 'dashboard'].includes(propKey)) continue;
            // If our config key was in the provided prop data, then use the value
            // from the provided data
            if (Object.prototype.hasOwnProperty.call(propsData, propKey)) {
                config[propKey] = this[propKey];
            }
        }

        this.internal_config = config;
        this.original_config = Object.assign({}, config);

        MeadCommon.Flash.subscribe(this.uid, `${this.uid}:widget_config_update`, this.onConfigUpdated);
    },
    destroyed () {
        MeadCommon.Flash.unsubscribe(this.uid);
    },
}